import ThemeToggle from './components/ThemeToggle';
import { lazy, Suspense } from "react";
import BootScreen from "./components/BootScreen";
import Layout from "./components/Layout";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "sonner";
import "./App.css";
import "./premium.css";
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Bills = lazy(() => import("./pages/Bills"));
const Cards = lazy(() => import("./pages/Cards"));
const Accounts = lazy(() => import("./pages/Accounts"));
const Reports = lazy(() => import("./pages/Reports"));
const Assistant = lazy(() => import("./pages/Assistant"));
const Settings = lazy(() => import("./pages/Settings"));
const Alerts = lazy(() => import("./pages/Alerts"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PaymentStatus = lazy(() => import("./pages/PaymentStatus"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const ConfirmEmail = lazy(() => import("./pages/ConfirmEmail"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Planning = lazy(() => import("./pages/Planning"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const Admin = lazy(() => import("./pages/Admin"));
const Reconciliation = lazy(() => import("./pages/Reconciliation"));
const FiscalInvoices = lazy(() => import("./pages/FiscalInvoices"));

function Private({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <BootScreen />;
  if (!user) return <Navigate to="/login" replace />;
  // Conta nova sem assinatura: precisa concluir o pagamento antes de entrar.
  if (user.subscription_required && !user.subscription_access) return <Navigate to="/assinar" replace />;
  // Conta recém-criada: passa primeiro pelo passo a passo inicial.
  if (user.onboarding_status === "PENDING") return <Navigate to="/onboarding" replace />;
  // Switching pages keeps the menu on screen while the next page loads.
  return (
    <Layout>
      <Suspense fallback={<div className="text-muted py-10 text-center" role="status">Carregando…</div>}>{children}</Suspense>
    </Layout>
  );
}

function OnboardingRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <BootScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.subscription_required && !user.subscription_access) return <Navigate to="/assinar" replace />;
  return children;
}

function SubscribeRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <BootScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Módulos adicionais (vendidos à parte) só abrem quando o admin liberou a conta.
function ModuleOnly({ module, children }) {
  const { workspace, loading } = useAuth();
  if (loading) return <BootScreen />;
  if (!workspace?.modules?.[module]?.enabled) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <BootScreen />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <BootScreen />;
  if (!user?.is_platform_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter><ThemeToggle />
        <Toaster position="top-right" theme="dark" richColors />
        <Suspense fallback={<BootScreen />}>
        <Routes>
          <Route path="/" element={<Navigate to="/precos" replace />} />
          <Route path="/precos" element={<Pricing />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/esqueci-senha" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/redefinir-senha" element={<PublicOnly><ResetPassword /></PublicOnly>} />
          <Route path="/payment/success" element={<PaymentStatus status="success" />} />
          <Route path="/payment/pending" element={<PaymentStatus status="pending" />} />
          <Route path="/payment/cancel" element={<PaymentStatus status="cancel" />} />
          <Route path="/payment/failure" element={<PaymentStatus status="failure" />} />
          <Route path="/privacidade" element={<Privacy />} />
          <Route path="/termos" element={<Terms />} />
          <Route path="/confirmar-email" element={<ConfirmEmail />} />
          <Route path="/convite" element={<AcceptInvite />} />
          <Route path="/exclusao-de-dados" element={<DataDeletion />} />
          <Route path="/assinar" element={<SubscribeRoute><Subscribe /></SubscribeRoute>} />
          <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />
          <Route path="/dashboard" element={<Private><Dashboard /></Private>} />
          <Route path="/transactions" element={<Private><Transactions /></Private>} />
          <Route path="/planning" element={<Private><Planning /></Private>} />
          <Route path="/bills" element={<Private><Bills /></Private>} />
          <Route path="/cards" element={<Private><Cards /></Private>} />
          <Route path="/accounts" element={<Private><Accounts /></Private>} />
          <Route path="/reconciliation" element={<Private><Reconciliation /></Private>} />
          <Route path="/reports" element={<Private><Reports /></Private>} />
          <Route path="/assistant" element={<Private><Assistant /></Private>} />
          <Route path="/alerts" element={<Private><Alerts /></Private>} />
          <Route path="/settings" element={<Private><Settings /></Private>} />
          <Route path="/notas-fiscais" element={<Private><ModuleOnly module="fiscal_invoices"><FiscalInvoices /></ModuleOnly></Private>} />
          <Route path="/admin" element={<Private><AdminOnly><Admin /></AdminOnly></Private>} />
          <Route path="*" element={<Navigate to="/precos" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

